import { Icon, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { FC, useState, useEffect } from "react";
import { Formater, TypeRecords } from "src/framework/commons/Formater";
import { FullWidthButton } from "src/framework/components/buttons/FullWidthButton";
import { InputSelectRegiao } from "src/framework/components/forms/inputs/InputSelectRegiao";
import defaultListStore from "src/framework/store/DefaultListStore";
import { notifierStore } from "src/framework/store/NotifierStore";
import { confirmationDialog } from "src/framework/store/ConfirmationDialogStore";
import { RegiaoParametroComponente } from "../../types";
import { InputDecimal } from "src/framework/components/forms/inputs/InputDecimal";
import { ErrorValidate } from "src/components/pagamento/types";

export interface RegioesParametrosProperties {
  componenteId: string;
  parametros: RegiaoParametroComponente[];
  onChange: (parametros: RegiaoParametroComponente[]) => void;
}

interface NewValor {
  regiao_id: number;
  potencia_inicial: number;
  potencia_final: number;
}

interface ErrorNewValor {
  regiao_id: ErrorValidate;
  potencia_inicial: ErrorValidate;
  potencia_final: ErrorValidate;
}

const INITIAL_ERRO_STATE: ErrorNewValor = {
  regiao_id: {
    hasError: false,
    hintText: ""
  },
  potencia_inicial: {
    hasError: false,
    hintText: ""
  },
  potencia_final: {
    hasError: false,
    hintText: ""
  },
}

const INITIAL_VALOR_STATE: NewValor = {
  regiao_id: 0,
  potencia_inicial: 0.0,
  potencia_final: 0.0,
}

const RegioesParametros: FC<RegioesParametrosProperties> = ({ componenteId, parametros, onChange, }) => {
  const [parametrosState, setParametrosState] = useState<RegiaoParametroComponente[]>([]);
  const [errorState, setErrorState] = useState<ErrorNewValor>(INITIAL_ERRO_STATE);
  const [newValorState, setNewValorState] = useState<NewValor>(INITIAL_VALOR_STATE);

  useEffect(() => {
    setErrorState(INITIAL_ERRO_STATE);
  }, []);

  useEffect(() => {
    setParametrosState([...parametros]);
  }, [parametros]);

  const addRange = (): void => {
    const errorMessages = [];

    if (!newValorState.regiao_id) {
      errorMessages.push(`O campo "região" é obrigatório!`)
      setErrorState({ ...errorState, regiao_id: { hasError: true, hintText: "Requerido" } });
    }

    if (newValorState.potencia_inicial.toString() === "") {
      errorMessages.push(`O campo "potência mínima" é obrigatório!`)
      setErrorState({ ...errorState, potencia_inicial: { hasError: true, hintText: "Requerido" } });
    }

    if (newValorState.potencia_final.toString() === "") {
      errorMessages.push(`O campo "potência máxima" é obrigatório!`)
      setErrorState({ ...errorState, potencia_final: { hasError: true, hintText: "Requerido" } });
    }

    if (Number(newValorState.potencia_final.toString()) > 0 && Number(newValorState.potencia_final.toString()) <= Number(newValorState.potencia_inicial.toString())) {
      errorMessages.push(`Potência máxima é menor/igual ao Potência mínimo.`);
      setErrorState({ ...errorState, potencia_inicial: { hasError: true, hintText: "Requerido" }, potencia_final: { hasError: true, hintText: "Requerido" } });
    }

    if (Number(newValorState.potencia_inicial.toString()) === 0 && Number(newValorState.potencia_final.toString()) === 0) {
      errorMessages.push(`Potência mínima e/ou máxima deverá ser maior que zero.`);
      setErrorState({ ...errorState, potencia_inicial: { hasError: true, hintText: "Requerido" }, potencia_final: { hasError: true, hintText: "Requerido" } });
    }

    if (errorMessages.length > 0) {
      notifierStore.setNotifier({
        title: "Aviso!",
        content: errorMessages,
        timeOut: 3500,
        onClose: () => {
          notifierStore.setOpen(false);
        }
      })
      notifierStore.setOpen(true);
      return;
    }

    const valoresCustom = [...parametrosState];
    const regioesParametros = valoresCustom.map((p) => {
      return p.regiao_id;
    });

    if (regioesParametros.includes(newValorState.regiao_id)) {
      const idx = valoresCustom.findIndex((p) => p.regiao_id === newValorState.regiao_id);

      if (idx > -1) {
        if (valoresCustom[idx]["@remove"]) {
          delete valoresCustom[idx]["@remove"];
        }

        valoresCustom[idx].potencia_inicial = newValorState.potencia_inicial;
        valoresCustom[idx].potencia_final = newValorState.potencia_final;
      }

    } else {
      valoresCustom.push({
        regiao_id: newValorState.regiao_id,
        potencia_inicial: Number(newValorState.potencia_inicial.toString()),
        potencia_final: Number(newValorState.potencia_final.toString())
      });
    }

    setParametrosState(valoresCustom);
    onChange(valoresCustom);
    setNewValorState(INITIAL_VALOR_STATE)
  }

  const removeRange = (idx: number): void => {
    confirmationDialog.setup({
      title: "Aviso!",
      content: "Deseja remover a região?",
      onClose: () => {
        confirmationDialog.close()
      },
      onConfirmation: () => {
        const valoresCustom: RegiaoParametroComponente[] = [...parametrosState];

        if (!!valoresCustom[idx]["created_at"] && valoresCustom[idx]["created_at"] !== null) {
          valoresCustom[idx]["@remove"] = true;
        } else {
          valoresCustom.splice(idx, 1)
        }

        setParametrosState(valoresCustom);
        onChange(valoresCustom);
        confirmationDialog.close()
      }
    })
    confirmationDialog.open()
  }

  return <>
    <div className="row X--middle X--start" style={{ marginTop: 8 }}>
      <div className="X12 M2">
        <InputSelectRegiao
          margin="none"
          label={"Região"}
          id={"regiao-" + componenteId}
          list={defaultListStore.regioes}
          value={(newValorState.regiao_id || null)}
          allowNull={true}
          onChange={(value: number | null) => {
            setNewValorState({ ...newValorState, regiao_id: (value || 0) });
          }}
        />
      </div>
      <div className="X12 M2">
        <InputDecimal
          error={errorState.potencia_inicial.hasError}
          label="Potência mínima"
          sufix="KWp"
          id={"potencia-valor-min" + componenteId}
          decimal={2}
          maxLength={15}
          value={newValorState.potencia_inicial}
          onFocus={() => {
            setErrorState({ ...errorState, potencia_inicial: { hasError: false, hintText: "" } });
          }}
          onChange={(event: any) => {
            setNewValorState({ ...newValorState, potencia_inicial: event.target.value })
          }}
        />
      </div>
      <div className="X12 M2">
        <InputDecimal
          error={errorState.potencia_final.hasError}
          label="Potência máxima"
          sufix="KWp"
          id={"potencia-valor-max" + componenteId}
          decimal={2}
          maxLength={15}
          value={newValorState.potencia_final}
          onFocus={() => {
            setErrorState({ ...errorState, potencia_final: { hasError: false, hintText: "" } });
          }}
          onChange={(event: any) => {
            setNewValorState({ ...newValorState, potencia_final: event.target.value })
          }}
        />
      </div>
      <div className="X12 M2">
        <FullWidthButton
          startIcon={<Icon>add</Icon>}
          text={"Adicionar"}
          color={"primary"}
          onClick={() => {
            addRange();
          }}
        />
      </div>
    </div>
    <div className="row">
      <div className="X12 M12">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Região</TableCell>
                <TableCell align="right">Potência Mínimo</TableCell>
                <TableCell align="right">Potência Máximo</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {parametrosState.map((range: RegiaoParametroComponente, idx: number) => {
                if (Object.keys(range).includes("@remove")) {
                  return "";
                }

                const idxRegiao = defaultListStore.regioes.findIndex((rec: any) => rec.id === range.regiao_id)

                return (
                  <TableRow key={`row-valor-${componenteId}-${idx}`}>
                    <TableCell>{idxRegiao ? defaultListStore.regioes[idxRegiao].nome : ""}</TableCell>
                    <TableCell align="right">{(Number(range.potencia_inicial) === 0 ? "Sem limite" : Formater.toString(range.potencia_inicial, TypeRecords.DECIMAL3) + " KWp")}</TableCell>
                    <TableCell align="right">{(Number(range.potencia_final) === 0 ? "Sem limite" : Formater.toString(range.potencia_final, TypeRecords.DECIMAL3) + " KWp")}</TableCell>
                    <TableCell align="right">
                      <IconButton size={"small"} onClick={() => removeRange(idx)}>
                        <Icon>delete</Icon>
                      </IconButton>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </div>
  </>
}

export { RegioesParametros }